/**
 * Panneau commentaires pour une actualité — utilisable dans la modale détail.
 * Liste + formulaire d'ajout + suppression de ses propres commentaires.
 */
import React, { useEffect, useState } from 'react';
import { MessageCircle, Send, Trash2, Loader2 } from 'lucide-react';
import { portailApi } from '../services/portailApi';

interface CommentaireItem {
  id: number;
  auteur_type: string;
  auteur_nom: string;
  contenu: string;
  date_creation: string;
  a_moi: boolean;
  parent_id_commentaire: number | null;
}

interface Props {
  actualiteId: number;
}

function relatif(iso: string): string {
  const s = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - s) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  const j = Math.floor(diff / 86400);
  if (j < 7) return `il y a ${j} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

const BADGE_ROLE: Record<string, string> = {
  etudiant: 'bg-blue-500/10 text-blue-300 border-blue-500/40',
  formateur: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40',
  parent: 'bg-purple-500/10 text-purple-300 border-purple-500/40',
  admin: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
};

export const CommentairesActualite: React.FC<Props> = ({ actualiteId }) => {
  const [items, setItems] = useState<CommentaireItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [texte, setTexte] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const charger = async () => {
    setLoading(true);
    try {
      const r = await portailApi.actualiteCommentairesList(actualiteId);
      setItems(r.results);
    } catch { /* silencieux */ } finally { setLoading(false); }
  };

  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [actualiteId]);

  const publier = async (e: React.FormEvent) => {
    e.preventDefault();
    const t = texte.trim();
    if (!t) return;
    setSending(true);
    try {
      await portailApi.actualiteCommentaireCreer(actualiteId, t);
      setTexte('');
      await charger();
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const supprimer = async (c: CommentaireItem) => {
    if (!c.a_moi) return;
    setDeletingId(c.id);
    try {
      await portailApi.actualiteCommentaireSupprimer(c.id);
      setItems((prev) => prev.filter((x) => x.id !== c.id));
    } catch { /* ignore */ } finally { setDeletingId(null); }
  };

  return (
    <div data-commentaires className="mt-4 pt-4 border-t border-gray-800">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
        <MessageCircle size={13} />
        {items.length === 0
          ? 'Soyez le premier à commenter'
          : `${items.length} commentaire${items.length > 1 ? 's' : ''}`}
      </div>

      {loading ? (
        <div className="py-4 text-center text-gray-500 text-xs">
          <Loader2 size={14} className="inline animate-spin" />
        </div>
      ) : (
        <div className="space-y-2.5 max-h-72 overflow-y-auto">
          {items.map((c) => (
            <div key={c.id} className="bg-gray-950 border border-gray-800 rounded-lg p-2.5">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-semibold text-gray-200">{c.auteur_nom}</span>
                <span className={`inline-flex px-1.5 py-0 text-[9px] font-semibold rounded border ${
                  BADGE_ROLE[c.auteur_type] || BADGE_ROLE.admin
                }`}>
                  {c.auteur_type}
                </span>
                <span className="text-[10px] text-gray-500 ml-auto">{relatif(c.date_creation)}</span>
                {c.a_moi && (
                  <button
                    onClick={() => supprimer(c)}
                    disabled={deletingId === c.id}
                    className="text-gray-500 hover:text-red-400 disabled:opacity-50"
                    title="Supprimer"
                  >
                    {deletingId === c.id
                      ? <Loader2 size={11} className="animate-spin" />
                      : <Trash2 size={11} />}
                  </button>
                )}
              </div>
              <div className="text-xs text-gray-300 whitespace-pre-wrap text-justify">
                {c.contenu}
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={publier} className="mt-3 flex gap-2">
        <input
          type="text"
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder="Écrire un commentaire…"
          maxLength={2000}
          className="flex-1 px-3 py-2 bg-gray-950 border border-gray-800 rounded text-xs text-gray-100 placeholder-gray-500 focus:border-amber-600 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!texte.trim() || sending}
          className="px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-black text-xs font-semibold rounded inline-flex items-center gap-1"
        >
          {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
        </button>
      </form>
    </div>
  );
};
